import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { blogData } from "../Blog/BlogData"; 
import {
  Calendar,
  User,
  Facebook,
  Twitter,
  Linkedin,
  ArrowLeft,
  Scale,
  MessageSquare,
  Share2
} from "lucide-react";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import { Helmet } from "react-helmet";

const BlogDetails = () => {
  // Exact Brand and Color Palette from Logo
  const COLORS = {
    red: "#841619",       // Deep Crimson Red
    cream: "#F2EBE1",     // Warm Premium Cream
    gold: "#C89B3C",      // Luxury Gold
    textDark: "#1A1A1A",  // Deep Charcoal
    textLight: "#FFFFFF", // Pure White
  };

  const { blogId } = useParams();
  const blog = blogData.find((b) => b.id === blogId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [blogId]);

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center font-serif" style={{ backgroundColor: COLORS.cream }}>
        <h2 className="text-4xl mb-6 font-bold" style={{ color: COLORS.red }}>Article Not Found</h2>
        <Link to="/blogs" className="uppercase tracking-[0.2em] text-sm font-bold hover:underline" style={{ color: COLORS.gold }}>
          Return to Journal
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.title} | R & P Law Partners</title>
        <meta name="description" content={blog.excerpt} />
        <meta name="author" content={blog.author} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:image" content={blog.image} />
      </Helmet>
      
      <Header />

      <main className="font-serif selection:bg-[#841619] selection:text-white" style={{ backgroundColor: COLORS.cream }}>
        
        {/* =========================================
            EDITORIAL HERO SECTION (40/60 Split)
            ========================================= */}
        <section className="w-full flex flex-col lg:flex-row min-h-[85vh] pt-24 lg:pt-0 items-stretch">
          
          {/* Left: 40% Red (Title & Meta) */}
          <div 
            className="w-full lg:w-[40%] flex flex-col justify-center px-8 py-16 lg:px-16 xl:px-20 relative z-10 shadow-[20px_0_30px_rgba(0,0,0,0.05)]"
            style={{ backgroundColor: COLORS.red }}
          >
            {/* Back Button */}
            <Link
              to="/blogs"
              className="group inline-flex items-center gap-2 mb-16 font-sans uppercase text-[10px] font-bold tracking-[3px] transition-all w-max"
              style={{ color: COLORS.gold }}
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-2 transition-transform" />
              Back to Journal
            </Link>

            <div className="flex items-center gap-4 mb-8">
              <span 
                className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-[3px] bg-white/10 backdrop-blur-sm border border-white/20"
                style={{ color: COLORS.textLight }}
              >
                {blog.category}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] mb-10" style={{ color: COLORS.textLight }}>
              {blog.title}
            </h1>

            <div className="flex flex-col gap-4 text-sm font-sans uppercase tracking-[2px] mt-auto pt-12 border-t border-white/10">
              <span className="flex items-center gap-3" style={{ color: COLORS.textLight }}>
                <User size={16} style={{ color: COLORS.gold }} /> By {blog.author}
              </span>
              <span className="flex items-center gap-3 text-white/70">
                <Calendar size={16} style={{ color: COLORS.gold }} /> 
                {new Date(blog.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>

          {/* Right: 60% Cream (Featured Image - Clean & No Overlays) */}
          <div 
            className="w-full lg:w-[60%] flex items-center justify-center p-8 md:p-16 relative"
            style={{ backgroundColor: COLORS.cream }}
          >
            {/*
              FIX: No fixed aspect-ratio box and no object-cover (both of
              which crop the image to fit a shape). Instead the wrapper
              just sizes itself to the image's natural proportions
              (w-full + h-auto on the img) capped by max-h/max-w, and
              object-contain ensures the ENTIRE image is always visible,
              never cropped, on any screen size.
            */}
            <div className="w-full max-w-[600px] relative">
              {/* Minimal Gold Offset Frame */}
              <div 
                className="absolute inset-0 border-[2px] translate-x-4 translate-y-4 lg:translate-x-6 lg:translate-y-6 pointer-events-none"
                style={{ borderColor: COLORS.gold }}
              ></div>
              <img
                src={blog.image}
                alt={blog.title}
                className="w-full h-auto max-h-[70vh] object-contain relative z-10 shadow-xl"
              />
            </div>
          </div>
        </section>

        {/* =========================================
            CONTENT & SIDEBAR (Bento Style layout)
            ========================================= */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 xl:gap-24">
            
            {/* Article Content */}
            <div className="lg:col-span-8">
              <div className="legal-journal-content-container">
                <div
                  className="article-body-text"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
              </div>

              {/* Share & Brand Footer */}
              <div className="mt-20 p-8 rounded-xl border border-black/5 bg-white flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F2EBE1]">
                    <Share2 size={20} style={{ color: COLORS.red }} />
                  </div>
                  <div>
                    <p className="font-bold text-sm uppercase tracking-widest" style={{ color: COLORS.textDark }}>Share Article</p>
                    <p className="text-xs font-sans text-gray-500">Distribute this insight.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  {[Facebook, Twitter, Linkedin].map((Icon, idx) => (
                      <button key={idx} className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:-translate-y-1 transition-all hover:border-[#841619] hover:bg-[#841619] hover:text-white text-[#1A1A1A]">
                        <Icon size={16} />
                      </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar Container */}
            <aside className="lg:col-span-4">
              <div className="sticky top-32 flex flex-col gap-8">
                
                {/* Premium Consultation Card */}
                <div className="p-8 md:p-10 bg-white border border-black/5 shadow-lg relative overflow-hidden group">
                  {/* Subtle hover accent line */}
                  <div className="absolute top-0 left-0 w-full h-1 transition-transform origin-left scale-x-0 group-hover:scale-x-100 duration-500" style={{ backgroundColor: COLORS.red }}></div>
                  
                  <h3 className="text-2xl font-bold mb-4" style={{ color: COLORS.textDark }}>
                    Require Legal Counsel?
                  </h3>
                  <div className="w-12 h-[2px] mb-6" style={{ backgroundColor: COLORS.gold }}></div>
                  
                  <p className="text-sm font-sans mb-8 leading-relaxed text-gray-600">
                    Connect with our senior Team for a confidential evaluation of your legal standing. Precision and discretion guaranteed.
                  </p>

                  <Link to="/contact-us" className="block w-full">
                    <button 
                        className="w-full border-2 font-bold py-4 uppercase text-[10px] tracking-[3px] transition-all duration-300"
                        style={{ borderColor: COLORS.textDark, color: COLORS.textDark }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.textDark;
                          e.target.style.color = COLORS.textLight;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.color = COLORS.textDark;
                        }}
                    >
                      Book Consultation
                    </button>
                  </Link>

                  <ul className="mt-8 space-y-4 text-xs font-sans font-medium text-gray-500 border-t border-black/5 pt-8">
                    <li className="flex items-center gap-3">
                      <MessageSquare size={14} style={{ color: COLORS.gold }} /> 
                      Secure & Confidential
                    </li>
                    <li className="flex items-center gap-3">
                      <Scale size={14} style={{ color: COLORS.gold }} /> 
                      Expert Representation
                    </li>
                  </ul>
                </div>

                {/* Categories / Tags Bento Box */}
                <div className="p-8 bg-white border border-black/5 shadow-sm">
                  <h4 className="font-bold mb-6 text-xs uppercase tracking-widest flex items-center gap-3" style={{ color: COLORS.red }}>
                    <div className="w-2 h-2 rotate-45" style={{ backgroundColor: COLORS.gold }}></div>
                    Practice Areas
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Criminal Cases",
                      "Civil Cases",
                      "Matrimonial Cases",
                      "Immigration Law",
                      "Company Related",
                      "Commercial Disputes",
                      "Cyber Crime",
                      "Labor Law",
                      "Negotiable Instrument Act Cases",
                      "IPR",
                      "Copyright & Trademark",
                      "Real Estate",
                    ].map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] font-sans border px-3 py-1.5 uppercase cursor-pointer transition-colors"
                        style={{ borderColor: "rgba(0,0,0,0.1)", color: COLORS.textDark }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.cream;
                          e.target.style.borderColor = COLORS.gold;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.borderColor = "rgba(0,0,0,0.1)";
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

              </div>
            </aside>
          </div>
        </section>

        {/* =========================================
            DYNAMIC CSS FOR INJECTED HTML
            ========================================= */}
        <style dangerouslySetInnerHTML={{ __html: `
            .article-body-text {
              font-family: ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
              line-height: 2;
              font-size: 1.15rem;
              color: ${COLORS.textDark};
            }
            
            /* High-End Drop Cap for the first letter of the article */
            .article-body-text > p:first-of-type::first-letter {
              font-size: 4.5rem;
              float: left;
              line-height: 0.8;
              margin-right: 0.15em;
              margin-top: 0.05em;
              color: ${COLORS.red};
              font-weight: 700;
            }

            .article-body-text p {
              margin-bottom: 2rem;
              opacity: 0.85;
            }
            
            .article-body-text h2 {
              font-size: 2.2rem;
              color: ${COLORS.textDark};
              font-weight: 700;
              margin: 4rem 0 1.5rem;
              line-height: 1.2;
            }
            
            .article-body-text h3 {
              font-size: 1.6rem;
              color: ${COLORS.red};
              font-weight: 700;
              margin: 2.5rem 0 1rem;
            }
            
            .article-body-text strong {
              color: ${COLORS.red};
              font-weight: bold;
            }
            
            .article-body-text blockquote {
              margin: 3.5rem 0;
              padding: 2.5rem 3rem;
              background: #FFFFFF;
              border-left: 4px solid ${COLORS.gold};
              font-style: italic;
              font-size: 1.4rem;
              color: ${COLORS.textDark};
              box-shadow: 0 10px 30px rgba(0,0,0,0.03);
              position: relative;
            }

            .article-body-text blockquote::before {
              content: '"';
              font-size: 5rem;
              color: ${COLORS.cream};
              position: absolute;
              top: -10px;
              left: 20px;
              font-family: sans-serif;
              z-index: 0;
            }
            
            .article-body-text blockquote p {
              position: relative;
              z-index: 1;
              margin-bottom: 0;
            }

            .article-body-text ul {
              margin: 2rem 0;
              padding: 0;
            }
            
            .article-body-text li {
              position: relative;
              padding-left: 2rem;
              margin-bottom: 1rem;
              list-style: none;
              font-family: 'Inter', sans-serif;
              font-size: 1rem;
            }
              a{
              color : blue;
              }
            
            .article-body-text li::before {
              content: "";
              position: absolute;
              left: 0;
              top: 12px;
              width: 12px;
              height: 2px;
              background: ${COLORS.gold};
            }
        `}} />
      </main>

      <Footer />
    </>
  );
};

export default BlogDetails;